import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchThreatFeeds, fetchIocMatches, fetchDarkWebMentions, fetchWatchlistKeywords,
  createThreatFeed, updateThreatFeed, syncThreatFeed,
  createIoc, deactivateIoc,
  createWatchlistKeyword, deleteWatchlistKeyword, triggerDarkWebScan,
  updateDarkWebMentionStatus,
} from '../../services/api/threatIntelService';

export const loadThreatIntel = createAsyncThunk('threatIntel/load', async () => {
  const [feeds, iocs, darkWeb, watchlist] = await Promise.all([
    fetchThreatFeeds(), fetchIocMatches(), fetchDarkWebMentions(), fetchWatchlistKeywords(),
  ]);
  return { feeds, iocs, darkWeb, watchlist };
});

export const addThreatFeed = createAsyncThunk('threatIntel/addFeed', async (payload) => {
  await createThreatFeed(payload);
  return fetchThreatFeeds();
});

export const toggleThreatFeed = createAsyncThunk('threatIntel/toggleFeed', async ({ id, enabled }) => {
  await updateThreatFeed(id, { enabled });
  return fetchThreatFeeds();
});

export const runFeedSync = createAsyncThunk('threatIntel/syncFeed', async (id) => {
  await syncThreatFeed(id);
  return fetchThreatFeeds();
});

export const addIoc = createAsyncThunk('threatIntel/addIoc', async (payload) => {
  await createIoc(payload);
  return fetchIocMatches();
});

export const removeIoc = createAsyncThunk('threatIntel/removeIoc', async (id) => {
  await deactivateIoc(id);
  return fetchIocMatches();
});

export const addWatchlistKeyword = createAsyncThunk('threatIntel/addWatchlistKeyword', async (payload) => {
  await createWatchlistKeyword(payload);
  return fetchWatchlistKeywords();
});

export const removeWatchlistKeyword = createAsyncThunk('threatIntel/removeWatchlistKeyword', async (id) => {
  await deleteWatchlistKeyword(id);
  return fetchWatchlistKeywords();
});

export const runDarkWebScan = createAsyncThunk('threatIntel/runDarkWebScan', async () => {
  await triggerDarkWebScan();
  return fetchDarkWebMentions();
});

export const setDarkWebMentionStatus = createAsyncThunk('threatIntel/setMentionStatus', async ({ id, status }) => {
  await updateDarkWebMentionStatus(id, status);
  return fetchDarkWebMentions();
});

const threatIntelSlice = createSlice({
  name: 'threatIntel',
  initialState: { status: 'idle', error: null, feeds: [], iocs: [], darkWeb: [], watchlist: [] },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadThreatIntel.pending, (state) => { state.status = 'loading'; })
      .addCase(loadThreatIntel.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.feeds = action.payload.feeds;
        state.iocs = action.payload.iocs;
        state.darkWeb = action.payload.darkWeb;
        state.watchlist = action.payload.watchlist;
      })
      .addCase(loadThreatIntel.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(addThreatFeed.fulfilled, (state, action) => { state.feeds = action.payload; })
      .addCase(toggleThreatFeed.fulfilled, (state, action) => { state.feeds = action.payload; })
      .addCase(runFeedSync.fulfilled, (state, action) => { state.feeds = action.payload; })
      .addCase(addIoc.fulfilled, (state, action) => { state.iocs = action.payload; })
      .addCase(removeIoc.fulfilled, (state, action) => { state.iocs = action.payload; })
      .addCase(addWatchlistKeyword.fulfilled, (state, action) => { state.watchlist = action.payload; })
      .addCase(removeWatchlistKeyword.fulfilled, (state, action) => { state.watchlist = action.payload; })
      .addCase(runDarkWebScan.fulfilled, (state, action) => { state.darkWeb = action.payload; })
      .addCase(setDarkWebMentionStatus.fulfilled, (state, action) => { state.darkWeb = action.payload; });
  }
});

export default threatIntelSlice.reducer;
